// V5.3 · Central de Lançamentos e Pagamentos — visão administrativa READ-ONLY.
// Fase 1: visualização + auditoria (nenhum delete/estorno é executado aqui).
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  DollarSign, AlertTriangle, FileText, Search, ShieldCheck, Filter,
  Copy, Trash2, Info, ArrowRight, Download, ShieldAlert, Receipt,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATE_STYLES = {
  'Confirmado': 'bg-green-100 text-green-800 border-green-300',
  'Vínculo encontrado': 'bg-blue-100 text-blue-800 border-blue-300',
  'Atenção': 'bg-amber-100 text-amber-800 border-amber-300',
  'Órfão real': 'bg-orange-100 text-orange-800 border-orange-300',
  'Suspeito / legado': 'bg-red-100 text-red-800 border-red-300',
  'Protegido': 'bg-blue-100 text-blue-800 border-blue-300',
  'Excluído': 'bg-gray-100 text-gray-800 border-gray-300',
};

const KIND_LABEL = {
  title: 'Título',
  event: 'Evento',
  dnapay_orphan: 'Boleto órfão',
  imported_pending: 'Importação',
};

function money(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  return s.length >= 10 ? s.substring(0, 10) : s;
}

export default function LancamentosCentral() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [origins, setOrigins] = useState([]);

  // Filtros
  const [tab, setTab] = useState('todos'); // todos|pagamentos|titulos|ajustes|suspeitos|excluidos
  const [q, setQ] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [origin, setOrigin] = useState('all');
  const [state, setState] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all'); // sem_vinculo|orfaos_reais|ambiguos|pagos|pendentes|vencidos|teste_legado|safra|cnab|excluidos
  const [limit] = useState(500);

  // Detalhes
  const [chain, setChain] = useState(null);
  const [chainOpen, setChainOpen] = useState(false);
  const [chainLoading, setChainLoading] = useState(false);

  // Ações (arquivar/restaurar/estornar)
  const [actionOpen, setActionOpen] = useState(false);
  const [actionRow, setActionRow] = useState(null);
  const [actionType, setActionType] = useState('archive'); // archive|restore|reverse-payment
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadAll();
  }, [tab, year, origin, state, quickFilter]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        year: year && year !== 'all' ? year : undefined,
        origin: origin !== 'all' ? origin : undefined,
        state: state !== 'all' ? state : undefined,
        quick_filter: quickFilter !== 'all' ? quickFilter : undefined,
        include_events: tab === 'todos' || tab === 'pagamentos' || tab === 'ajustes',
        include_orphans: tab === 'todos' || tab === 'suspeitos' || quickFilter === 'orfaos_reais' || quickFilter === 'ambiguos' || quickFilter === 'sem_vinculo' || quickFilter === 'safra',
        include_imported: tab === 'todos' || tab === 'suspeitos',
        include_deleted: tab === 'excluidos' || quickFilter === 'excluidos',
        only_suspicious: tab === 'suspeitos',
      };
      const [statsRes, entriesRes, originsRes] = await Promise.all([
        api.get('/finance/entries/stats').catch(() => ({ data: null })),
        api.get('/finance/entries', { params }),
        api.get('/finance/entries/origins').catch(() => ({ data: { origins: [] } })),
      ]);
      if (statsRes.data) setStats(statsRes.data);
      const list = entriesRes.data.entries || [];
      setEntries(applyTabFilter(list, tab));
      setTotal(entriesRes.data.total || 0);
      setOrigins(originsRes.data.origins || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar Central de Lançamentos');
    } finally {
      setLoading(false);
    }
  };

  const applyTabFilter = (list, t) => {
    if (t === 'pagamentos') {
      return list.filter((r) => r.kind === 'event' && String(r.event_type || '').includes('PAYMENT'));
    }
    if (t === 'titulos') {
      return list.filter((r) => r.kind === 'title');
    }
    if (t === 'ajustes') {
      return list.filter((r) => r.kind === 'event' && !String(r.event_type || '').includes('PAYMENT'));
    }
    if (t === 'suspeitos') {
      return list.filter((r) => r.state === 'Suspeito / legado' || r.state === 'Atenção');
    }
    if (t === 'excluidos') {
      return list.filter((r) => r.deleted_at);
    }
    return list;
  };

  const filteredBySearch = useMemo(() => {
    if (!q) return entries;
    const ql = q.toLowerCase();
    return entries.filter((r) =>
      ['student_name', 'responsible_name', 'description', 'title_id', 'nosso_numero', 'id']
        .some((k) => String(r[k] || '').toLowerCase().includes(ql))
    );
  }, [entries, q]);

  const openChain = async (row) => {
    const tid = row.title_id || row.id;
    if (!tid) return;
    setChainOpen(true);
    setChainLoading(true);
    try {
      const res = await api.get(`/finance/entries/${tid}/chain`);
      setChain(res.data);
    } catch (err) {
      toast.error('Falha ao carregar cadeia do lançamento');
    } finally {
      setChainLoading(false);
    }
  };

  const openAction = (row, type) => {
    setActionRow(row); setActionType(type); setActionReason(''); setActionOpen(true);
  };

  const submitAction = async () => {
    if (!actionRow || !actionType) return;
    if (actionReason.trim().length < 3) { toast.error('Informe o motivo (mín. 3 caracteres).'); return; }
    const tid = actionRow.title_id || actionRow.id;
    setActionLoading(true);
    try {
      await api.post(`/finance/entries/${tid}/${actionType}`, { reason: actionReason.trim() });
      toast.success({
        archive: 'Título arquivado (soft-delete) com sucesso.',
        restore: 'Título restaurado com sucesso.',
        'reverse-payment': 'Pagamento estornado com sucesso.',
      }[actionType]);
      setActionOpen(false);
      await loadAll();
    } catch (err) {
      const d = err?.response?.data;
      const detail = typeof d?.detail === 'object' ? JSON.stringify(d.detail) : (d?.detail || err.message);
      toast.error(`Falha: ${detail}`);
    } finally {
      setActionLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = filteredBySearch;
    if (!rows.length) { toast.message('Nada para exportar'); return; }
    const cols = ['kind', 'title_id', 'date', 'due_date', 'payment_date', 'competence',
      'student_name', 'responsible_name', 'class_name', 'category', 'description',
      'origin', 'type', 'nosso_numero', 'amount_original', 'discount', 'paid_amount',
      'balance', 'status', 'state', 'created_by'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `central_lancamentos_${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6" data-testid="central-lancamentos">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-600" />
            Central de Lançamentos e Pagamentos
          </h1>
          <p className="text-muted-foreground">
            Visão única · <b>read-only na fase 1</b> · fonte canônica <code>db.titles + finance_events + dnapay_titulos</code>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="central-export">
            <Download className="w-4 h-4 mr-2" /> Exportar
          </Button>
          <Button variant="outline" onClick={loadAll}>
            <Loader2 className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>
      </div>

      {/* Aviso Fase 2 · limpeza segura */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-3 flex gap-3 items-start">
          <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>V5.4 · Limpeza segura habilitada.</b> Arquivar / Restaurar / Estornar Pagamento executam <b>soft-delete</b> e registram <code>finance_events</code> com <i>before</i>, <i>after</i>, usuário e motivo.
            Títulos com NFS-e emitida, retorno CNAB ou pagamento confirmado são bloqueados e exigem estorno explícito. <b>Hard delete não existe nesta tela.</b>
          </div>
        </CardContent>
      </Card>

      {/* Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard label="Total títulos" value={stats?.total_registros} testid="stat-total" />
        <StatCard label="Vinculados" value={stats?.vinculados} tone="ok" testid="stat-vinc" />
        <StatCard label="Órfãos reais" value={stats?.orfaos_reais} tone="warn" testid="stat-orf" />
        <StatCard label="Ambíguos" value={stats?.ambiguos} tone="warn" testid="stat-amb" />
        <StatCard label="Pagos" value={stats?.pagamentos_reais} testid="stat-pagos" />
        <StatCard label="Pendentes" value={stats?.pendentes} testid="stat-pend" />
        <StatCard label="Vencidos" value={stats?.vencidos} tone="warn" testid="stat-venc" />
        <StatCard label="Valor recebido" value={stats ? money(stats.valor_recebido) : '—'} testid="stat-receita" />
        <StatCard label="Suspeitos" value={stats?.registros_suspeitos} tone="warn" testid="stat-suspeitos" />
        <StatCard label="Duplicidades" value={stats?.duplicidades_provaveis} tone="warn" testid="stat-dups" />
        <StatCard label="Excluídos" value={stats?.registros_excluidos} tone="mute" testid="stat-excluidos" />
      </div>

      {/* Filtros rápidos por vínculo */}
      <div className="flex flex-wrap gap-2" data-testid="quick-filters">
        {[
          ['all', 'Todos'],
          ['sem_vinculo', 'Sem vínculo'],
          ['orfaos_reais', 'Órfãos reais'],
          ['ambiguos', 'Ambíguos'],
          ['pagos', 'Pagos'],
          ['pendentes', 'Pendentes'],
          ['vencidos', 'Vencidos'],
          ['teste_legado', 'Teste/Legado'],
          ['safra', 'Safra'],
          ['cnab', 'CNAB'],
          ['excluidos', 'Excluídos'],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setQuickFilter(k)}
            className={`px-3 py-1 text-xs rounded-full border transition ${
              quickFilter === k
                ? 'bg-purple-600 text-white border-purple-700'
                : 'bg-white hover:bg-purple-50 border-gray-200 text-gray-700'
            }`}
            data-testid={`quick-${k}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* Tabs + Filtros */}
      <Card>
        <CardContent className="pt-4 space-y-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex-wrap">
              <TabsTrigger value="todos" data-testid="tab-todos">Todos</TabsTrigger>
              <TabsTrigger value="pagamentos" data-testid="tab-pagamentos">Pagamentos</TabsTrigger>
              <TabsTrigger value="titulos" data-testid="tab-titulos">Títulos</TabsTrigger>
              <TabsTrigger value="ajustes" data-testid="tab-ajustes">Ajustes</TabsTrigger>
              <TabsTrigger value="suspeitos" data-testid="tab-suspeitos">
                Suspeitos {stats?.registros_suspeitos ? <Badge variant="destructive" className="ml-2">{stats.registros_suspeitos}</Badge> : null}
              </TabsTrigger>
              <TabsTrigger value="excluidos" data-testid="tab-excluidos">Excluídos</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="central-search"
                placeholder="Buscar por aluno, responsável, título, nosso número, descrição…"
                className="pl-9"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger><SelectValue placeholder="Ano" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os anos</SelectItem>
                {[2024, 2025, 2026, 2027].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={origin} onValueChange={setOrigin}>
              <SelectTrigger><SelectValue placeholder="Origem" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as origens</SelectItem>
                {origins.map((o) => (
                  <SelectItem key={o.name} value={o.name}>
                    {o.name} · {o.count}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger><SelectValue placeholder="Classificação" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas classificações</SelectItem>
                <SelectItem value="Confirmado">Confirmado</SelectItem>
                <SelectItem value="Atenção">Atenção</SelectItem>
                <SelectItem value="Suspeito / legado">Suspeito / legado</SelectItem>
                <SelectItem value="Protegido">Protegido</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Lançamentos</CardTitle>
            <CardDescription>
              {loading ? 'Carregando…' : `${filteredBySearch.length} exibidos · ${total} no total (limite ${limit})`}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Aluno / Responsável</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Classificação</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBySearch.map((r) => (
                    <TableRow key={`${r.kind}-${r.id}`} data-testid={`row-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {fmtDate(r.date || r.due_date)}
                        {r.competence && (
                          <div className="text-xs text-muted-foreground">{r.competence}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">{KIND_LABEL[r.kind] || r.kind}</Badge>
                        <div className="text-xs text-muted-foreground mt-0.5">{r.type}</div>
                      </TableCell>
                      <TableCell className="min-w-[200px]">
                        <div className="font-medium">{r.student_name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{r.responsible_name || '—'}</div>
                        {r.class_name && (
                          <div className="text-[10px] text-muted-foreground">{r.class_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <div className="text-sm truncate">{r.description || '—'}</div>
                        {r.nosso_numero && (
                          <div className="text-[10px] text-muted-foreground">NN {r.nosso_numero}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.origin}</Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <div className="font-medium">{money(r.paid_amount || r.amount_original || 0)}</div>
                        {r.balance > 0 && (
                          <div className="text-xs text-orange-600">Saldo: {money(r.balance)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.status || '—'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={STATE_STYLES[r.state] || ''} variant="outline">
                          {r.state === 'Protegido' && <ShieldCheck className="w-3 h-3 mr-1 inline" />}
                          {r.state === 'Suspeito / legado' && <AlertTriangle className="w-3 h-3 mr-1 inline" />}
                          {r.state}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openChain(r)}
                            title="Ver detalhes / cadeia completa"
                            data-testid={`row-chain-${r.id}`}
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Copiar ID"
                            onClick={() => { navigator.clipboard.writeText(r.id || ''); toast.message('ID copiado'); }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {r.kind === 'title' && !r.deleted_at && r.state !== 'Protegido' && String(r.status || '').toLowerCase() !== 'pago' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Arquivar (soft-delete)"
                              onClick={() => openAction(r, 'archive')}
                              data-testid={`row-archive-${r.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                          {r.kind === 'title' && String(r.status || '').toLowerCase() === 'pago' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Estornar pagamento"
                              onClick={() => openAction(r, 'reverse-payment')}
                              data-testid={`row-reverse-${r.id}`}
                            >
                              <Receipt className="w-4 h-4 text-orange-500" />
                            </Button>
                          )}
                          {r.deleted_at && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Restaurar"
                              onClick={() => openAction(r, 'restore')}
                              data-testid={`row-restore-${r.id}`}
                            >
                              <ArrowRight className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredBySearch.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                        Nenhum lançamento com os filtros atuais.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cadeia completa */}
      <Dialog open={chainOpen} onOpenChange={setChainOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="central-chain-dialog">
          <DialogHeader>
            <DialogTitle>Cadeia completa do lançamento</DialogTitle>
            <DialogDescription>
              Todas as entidades vinculadas ao <code>title_id</code>.
            </DialogDescription>
          </DialogHeader>
          {chainLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : chain ? (
            <ChainViewer chain={chain} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Ação com motivo obrigatório */}
      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent className="max-w-md" data-testid="central-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'archive' && 'Arquivar lançamento (soft-delete)'}
              {actionType === 'restore' && 'Restaurar lançamento'}
              {actionType === 'reverse-payment' && 'Estornar pagamento'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'archive' && 'O título é preservado no banco com deleted_at. Pode ser restaurado.'}
              {actionType === 'restore' && 'Reativa o título e limpa deleted_at.'}
              {actionType === 'reverse-payment' && 'Zera paid_amount, retorna o status para pendente/vencido e registra PAYMENT_REVERSED.'}
            </DialogDescription>
          </DialogHeader>
          {actionRow && (
            <div className="text-sm space-y-1 border rounded p-2 bg-muted/40">
              <div><b>Título:</b> <span className="font-mono text-xs">{actionRow.title_id || actionRow.id}</span></div>
              <div><b>Aluno:</b> {actionRow.student_name || '—'}</div>
              <div><b>Descrição:</b> {actionRow.description || '—'}</div>
              <div><b>Valor:</b> {money(actionRow.paid_amount || actionRow.amount_original || 0)}</div>
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium">Motivo *</label>
            <Select value={actionReason} onValueChange={setActionReason}>
              <SelectTrigger data-testid="action-reason-preset"><SelectValue placeholder="Escolha um motivo padrão…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Registro de teste">Registro de teste</SelectItem>
                <SelectItem value="Duplicidade">Duplicidade</SelectItem>
                <SelectItem value="Valor incorreto">Valor incorreto</SelectItem>
                <SelectItem value="Criado durante desenvolvimento">Criado durante desenvolvimento</SelectItem>
                <SelectItem value="Importação incorreta">Importação incorreta</SelectItem>
                <SelectItem value="Pagamento lançado indevidamente">Pagamento lançado indevidamente</SelectItem>
                <SelectItem value="Outro">Outro</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Ou digite um motivo detalhado (mín. 3 caracteres)"
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              data-testid="action-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setActionOpen(false)} disabled={actionLoading}>Cancelar</Button>
            <Button
              onClick={submitAction}
              disabled={actionLoading || actionReason.trim().length < 3}
              className={actionType === 'archive' ? 'bg-red-600 hover:bg-red-700' : actionType === 'reverse-payment' ? 'bg-orange-600 hover:bg-orange-700' : ''}
              data-testid="action-submit"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = tone === 'warn'
    ? 'border-orange-200 bg-orange-50'
    : tone === 'mute'
      ? 'border-gray-200 bg-gray-50'
      : tone === 'ok'
        ? 'border-green-200 bg-green-50'
        : 'border-purple-200 bg-purple-50';
  return (
    <Card className={toneCls} data-testid={testid}>
      <CardContent className="pt-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-bold mt-1">{value ?? '—'}</p>
      </CardContent>
    </Card>
  );
}

function P({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

function ChainViewer({ chain }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">Origem: {chain.origin}</Badge>
        <Badge className={STATE_STYLES[chain.state] || ''} variant="outline">{chain.state}</Badge>
        {chain.protection_reasons?.has_fiscal_nfse && <Badge className="bg-blue-100 text-blue-800">🔒 NFS-e emitida</Badge>}
        {chain.protection_reasons?.has_cnab_return && <Badge className="bg-blue-100 text-blue-800">🔒 Retorno CNAB</Badge>}
        {chain.protection_reasons?.has_payment_record && <Badge className="bg-blue-100 text-blue-800">🔒 Pagamento registrado</Badge>}
      </div>

      {chain.match_result && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-3 text-sm">
            <div className="font-medium mb-1">Análise de vínculo</div>
            <P label="Estratégia" value={chain.match_result.strategy || 'nenhuma'} />
            <P label="Estado" value={chain.match_result.match_state} />
            <P label="Candidatos" value={(chain.match_result.candidate_ids || []).length} />
            {chain.match_result.resolved_student_id && <P label="Aluno inferido" value={chain.match_result.resolved_student_id} />}
            {chain.match_result.resolved_responsible_id && <P label="Responsável inferido" value={chain.match_result.resolved_responsible_id} />}
          </CardContent>
        </Card>
      )}

      <Section title="Título" data={chain.title} keys={['id', 'description', 'amount', 'paid_amount', 'due_date', 'payment_date', 'status', 'financial_plan_id', 'category_id']} />
      <Section title="Aluno" data={chain.student} keys={['id', 'full_name', 'class_name', 'class_group_id', 'cpf', 'email']} />
      <Section title="Responsável" data={chain.responsible} keys={['id', 'full_name', 'name', 'cpf', 'email', 'phone']} />
      <Section title="Turma" data={chain.class_group} keys={['id', 'name', 'segment']} />
      <Section title="Plano" data={chain.plan} keys={['id', 'name', 'description', 'amount']} />

      <div>
        <h4 className="text-sm font-medium mb-2">Boletos DNAPay ({chain.boletos?.length || 0})</h4>
        {chain.boletos?.length ? chain.boletos.map((b, i) => (
          <Card key={i} className="mb-2"><CardContent className="pt-3 space-y-1">
            <P label="Nosso número" value={b.nosso_numero} />
            <P label="Vencimento" value={fmtDate(b.vencimento)} />
            <P label="Status" value={b.status} />
            <P label="Valor original" value={money(b.valor_original)} />
          </CardContent></Card>
        )) : <div className="text-xs text-muted-foreground">Sem boleto emitido.</div>}
      </div>

      <div>
        <h4 className="text-sm font-medium mb-2">Pagamentos ({chain.payments?.length || 0})</h4>
        {chain.payments?.length ? chain.payments.map((p, i) => (
          <Card key={i} className="mb-2"><CardContent className="pt-3 space-y-1">
            <P label="Data" value={fmtDate(p.payment_date || p.data)} />
            <P label="Valor" value={money(p.amount || p.valor)} />
            <P label="Forma" value={p.payment_method || p.forma_pgto} />
            <P label="Origem" value={p.origin || p.source} />
          </CardContent></Card>
        )) : <div className="text-xs text-muted-foreground">Sem pagamento registrado em <code>db.payments</code>.</div>}
      </div>

      <div>
        <h4 className="text-sm font-medium mb-2">NFS-e ({chain.fiscal_notas?.length || 0})</h4>
        {chain.fiscal_notas?.length ? chain.fiscal_notas.map((n, i) => (
          <Card key={i} className="mb-2"><CardContent className="pt-3 space-y-1">
            <P label="Número" value={n.numero} />
            <P label="Status" value={n.status} />
            <P label="Data emissão" value={fmtDate(n.data_emissao)} />
            <P label="Valor serviços" value={money(n.valor_servicos)} />
          </CardContent></Card>
        )) : <div className="text-xs text-muted-foreground">Sem NFS-e emitida.</div>}
      </div>

      <div>
        <h4 className="text-sm font-medium mb-2">Retornos CNAB ({chain.cnab_records?.length || 0})</h4>
        {chain.cnab_records?.length ? chain.cnab_records.map((c, i) => (
          <Card key={i} className="mb-2"><CardContent className="pt-3 space-y-1">
            <P label="Batch" value={c.batch_id} />
            <P label="Data ocorrência" value={fmtDate(c.data_ocorrencia)} />
            <P label="Valor" value={money(c.valor_pago || c.valor_pagamento)} />
          </CardContent></Card>
        )) : <div className="text-xs text-muted-foreground">Sem retorno CNAB conciliado.</div>}
      </div>

      <div>
        <h4 className="text-sm font-medium mb-2">finance_events ({chain.finance_events?.length || 0})</h4>
        {chain.finance_events?.length ? chain.finance_events.map((e, i) => (
          <Card key={i} className="mb-2"><CardContent className="pt-3 space-y-1">
            <P label="Tipo" value={e.event_type} />
            <P label="Data" value={fmtDate(e.created_at)} />
            <P label="Usuário" value={e.user_id} />
            <P label="Motivo" value={e.reason} />
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">before / after</summary>
              <pre className="mt-1 bg-muted p-2 rounded overflow-x-auto text-[10px]">{JSON.stringify({ before: e.before, after: e.after }, null, 2)}</pre>
            </details>
          </CardContent></Card>
        )) : <div className="text-xs text-muted-foreground">Sem eventos registrados.</div>}
      </div>
    </div>
  );
}

function Section({ title, data, keys }) {
  if (!data) return (
    <div>
      <h4 className="text-sm font-medium mb-1">{title}</h4>
      <div className="text-xs text-muted-foreground">Não vinculado.</div>
    </div>
  );
  return (
    <div>
      <h4 className="text-sm font-medium mb-2">{title}</h4>
      <Card><CardContent className="pt-3 space-y-1">
        {keys.map((k) => data[k] !== undefined && data[k] !== null && data[k] !== '' && (
          <P key={k} label={k} value={typeof data[k] === 'number' ? String(data[k]) : String(data[k])} />
        ))}
      </CardContent></Card>
    </div>
  );
}
